import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RegisterV2() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [brregData, setBrregData] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const [orgData, setOrgData] = useState({
    orgnr: '',
    name: '',
    industry: null,
    employees: null,
    address: null,
    website: '',
    description: ''
  });
  
  const [userData, setUserData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    phone: '',
    title: 'Daglig leder'
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const lookupBrreg = async (orgnr) => {
    if (!/^\d{9}$/.test(orgnr)) {
      setError('Organisasjonsnummer må være 9 siffer');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API}/brreg/${orgnr}`);
      setBrregData(response.data);
      
      setOrgData(prev => ({
        ...prev,
        orgnr: orgnr,
        name: response.data.name || prev.name,
        industry: response.data.branch_profile || prev.industry,
        employees: response.data.employee_count || prev.employees,
        address: response.data.address || prev.address
      }));
      
      setStep(2);
    } catch (error) {
      console.error('Brreg lookup error:', error);
      setError('Kunne ikke finne bedrift i Brønnøysundregisteret. Sjekk organisasjonsnummeret.');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Passordet må være minst 8 tegn';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Passordet må inneholde minst én stor bokstav';
    }
    if (!/[a-z]/.test(password)) {
      return 'Passordet må inneholde minst én liten bokstav';
    }
    if (!/[0-9]/.test(password)) {
      return 'Passordet må inneholde minst ett tall';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate password
    const passwordError = validatePassword(userData.password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }
    
    // Check if passwords match
    if (userData.password !== userData.confirmPassword) {
      setError('Passordene stemmer ikke overens');
      setLoading(false);
      return;
    }

    // Check if terms are accepted
    if (!acceptedTerms) {
      setError('Du må godta vilkårene og personvernerklæringen for å registrere deg');
      setLoading(false);
      return;
    }
    
    try {
      const result = await register(orgData, userData);
      
      if (result.success) {
        navigate('/onboarding');
      } else {
        setError(result.error || 'Registrering feilet');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('En feil oppstod under registrering');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklart Logo" 
                className="h-10"
              />
              <span className="text-gray-500">Registrering</span>
            </Link>
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/eui6rlb9_Hovedlogo%20Valoria%20%28uten%20bakgrunn%29.png" 
                alt="Valoria Solutions" 
                className="h-8"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-center py-12">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full mx-4">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className={`flex-1 ${
                step >= 1 ? 'text-blue-600' : 'text-gray-400'
              }`}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    1
                  </div>
                  <span className="ml-2 text-sm font-medium">Organisasjon</span>
                </div>
              </div>
              <div className="flex-1 border-t-2 border-gray-300 mx-4"></div>
              <div className={`flex-1 ${
                step >= 2 ? 'text-blue-600' : 'text-gray-400'
              }`}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    2
                  </div>
                  <span className="ml-2 text-sm font-medium">Administrator</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 1: Organization */}
          {step === 1 && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Registrer bedrift
                </h2>
                <p className="text-gray-600 mt-2">
                  Skriv inn organisasjonsnummeret for å hente informasjon fra Brønnøysundregisteret
                </p>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                lookupBrreg(orgData.orgnr);
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Organisasjonsnummer *
                    </label>
                    <input
                      type="text"
                      required
                      pattern="\d{9}"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={orgData.orgnr}
                      onChange={(e) => setOrgData({...orgData, orgnr: e.target.value})}
                      placeholder="123456789"
                      data-testid="register-orgnr"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600" data-testid="register-error">
                        {error}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="register-lookup-button"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Søker...
                      </>
                    ) : (
                      'Søk i Brønnøysundregisteret'
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-blue-600 hover:text-blue-500 text-sm">
                  Har du allerede konto? Logg inn her
                </Link>
              </div>
            </div>
          )}

          {/* Step 2: Admin User */}
          {step === 2 && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Administrator
                </h2>
                <p className="text-gray-600 mt-2">
                  Opprett administratorkonto for {orgData.name}
                </p>
              </div>

              {/* Organization Summary */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Bedriftsinformasjon</h3>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><strong>Navn:</strong> {orgData.name}</p>
                  <p><strong>Org.nr:</strong> {orgData.orgnr}</p>
                  {orgData.industry && <p><strong>Bransje:</strong> {orgData.industry}</p>}
                  {orgData.employees && <p><strong>Ansatte:</strong> {orgData.employees}</p>}
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="mt-3 text-blue-600 hover:text-blue-500 text-sm font-medium"
                >
                  Endre organisasjon
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Fullt navn *
                    </label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={userData.name}
                      onChange={(e) => setUserData({...userData, name: e.target.value})}
                      placeholder="Ola Nordmann"
                      data-testid="register-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      E-postadresse *
                    </label>
                    <input
                      type="email"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={userData.email}
                      onChange={(e) => setUserData({...userData, email: e.target.value})}
                      placeholder="ola@bedrift.no"
                      data-testid="register-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Passord *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={userData.password}
                        onChange={(e) => setUserData({...userData, password: e.target.value})}
                        placeholder="••••••••"
                        data-testid="register-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Min. 8 tegn, må inneholde stor bokstav, liten bokstav og tall
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Bekreft passord *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={userData.confirmPassword}
                        onChange={(e) => setUserData({...userData, confirmPassword: e.target.value})}
                        placeholder="••••••••"
                        data-testid="register-confirm-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Telefonnummer
                    </label>
                    <input
                      type="tel"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={userData.phone}
                      onChange={(e) => setUserData({...userData, phone: e.target.value})}
                      placeholder="+47 123 45 678"
                      data-testid="register-phone"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Stillingstittel
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={userData.title}
                      onChange={(e) => setUserData({...userData, title: e.target.value})}
                      placeholder="Daglig leder"
                      data-testid="register-title"
                    />
                  </div>

                  {/* Terms acceptance checkbox */}
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        data-testid="register-accept-terms"
                      />
                      <span className="text-sm text-gray-700">
                        Jeg har lest og godtar{' '}
                        <Link to="/terms" target="_blank" className="text-blue-600 hover:underline font-medium">
                          vilkårene for bruk
                        </Link>
                        {' '}og{' '}
                        <Link to="/privacy" target="_blank" className="text-blue-600 hover:underline font-medium">
                          personvernerklæringen
                        </Link>
                        . Jeg forstår at Systemklar er et støtteverktøy og at jeg er ansvarlig for å 
                        kvalitetssikre alt generert innhold.
                        <span className="text-red-500"> *</span>
                      </span>
                    </label>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600" data-testid="register-error">
                        {error}
                      </p>
                    </div>
                  )}

                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Tilbake
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="register-submit"
                    >
                      {loading ? 'Registrerer...' : 'Fullfør registrering'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RegisterV2;